import { memo } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { CommonDialog } from "@/components/common/CommonDialog";
import { PrimaryButton } from "@/components/common/PrimaryButton";
import { SecondaryButton } from "@/components/common/SecondaryButton";
import type { PurchaseReleaseLineInput } from "src-next/modules/patient-supporting-documents";

interface ConfirmReleaseDialogProps {
  name: string;
  lines: PurchaseReleaseLineInput[];
  validUntil: string;
  submitting: boolean;
  today: Dayjs;
  onValidUntilChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
}

export const ConfirmReleaseDialog = memo(function ConfirmReleaseDialog({
  name,
  lines,
  validUntil,
  submitting,
  today,
  onValidUntilChange,
  onClose,
  onConfirm,
}: ConfirmReleaseDialogProps) {
  const validUntilValue = validUntil ? dayjs(validUntil) : null;

  return (
    <CommonDialog
      isOpen
      handleClose={() => {
        if (!submitting) onClose();
      }}
      maxWidth="sm"
      sheetTitle="Confirmar liberação"
      sheetSubtitle={name}
      footer={
        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          spacing={1}
          justifyContent="flex-end"
        >
          <SecondaryButton
            width="fit-content"
            height="40px"
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </SecondaryButton>
          <PrimaryButton
            width="fit-content"
            height="40px"
            disabled={!validUntil || submitting}
            onClick={onConfirm}
            startIcon={<CheckCircleOutlineIcon />}
          >
            {submitting ? "Gravando…" : "Confirmar liberação"}
          </PrimaryButton>
        </Stack>
      }
    >
      <Box sx={{ px: { xs: 0, md: 1 }, pb: 1 }}>
        <Stack spacing={2}>
          <Alert severity="warning" icon={<InfoOutlinedIcon />}>
            Informe até quando o paciente pode comprar. Essa data vale para
            todos os produtos desta liberação.
          </Alert>

          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="pt-br"
          >
            <DatePicker
              label="Paciente pode comprar até"
              value={validUntilValue?.isValid() ? validUntilValue : null}
              onChange={(value: Dayjs | null) => {
                onValidUntilChange(
                  value?.isValid() ? value.format("YYYY-MM-DD") : "",
                );
              }}
              format="DD/MM/YYYY"
              minDate={today}
              disabled={submitting}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  required: true,
                  autoFocus: true,
                },
              }}
            />
          </LocalizationProvider>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Produtos ({lines.length})
            </Typography>
            <Stack
              spacing={0.75}
              sx={{
                maxHeight: 220,
                overflowY: "auto",
                border: 1,
                borderColor: "grey.200",
                borderRadius: 1,
                p: 1.25,
                bgcolor: "grey.50",
              }}
            >
              {lines.map((line) => (
                <Stack
                  key={line.product.id}
                  direction="row"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Typography variant="body2" sx={{ minWidth: 0 }} noWrap>
                    {line.product.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ flexShrink: 0 }}
                  >
                    Qtd. {line.quantity}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </CommonDialog>
  );
});
